if (!customElements.get('page-campaign-video')) {
  class PageCampaignVideo extends HTMLElement {
    connectedCallback() {
      this.mediaType = this.dataset.mediaType || 'image';
      this.videos = Array.from(this.querySelectorAll('[data-campaign-video]'));
      this.plyrHost = this.querySelector('[data-plyr-provider]');
      this.player = null;
      this.viewportObserver = null;
      this.prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      if (this.prefersReducedMotion) {
        this.closest('.page-campaign-video')?.classList.add('is-reduced-motion');
        return;
      }

      if (this.mediaType === 'video') {
        this.bindHtml5Videos();
        this.observeViewport();
        return;
      }

      if (this.mediaType === 'youtube' && this.plyrHost) {
        this.whenPlyrReady(() => {
          this.initYoutube();
          this.observeViewport();
        });
      }
    }

    disconnectedCallback() {
      this.viewportObserver?.disconnect();
      this.viewportObserver = null;

      if (this.motionQuery?.removeEventListener) {
        this.motionQuery.removeEventListener('change', this.onMotionChange);
      }

      if (this.player) {
        this.player.destroy();
        this.player = null;
      }
    }

    whenPlyrReady(callback) {
      if (typeof Plyr !== 'undefined') {
        callback();
        return;
      }

      const startedAt = Date.now();
      const timer = window.setInterval(() => {
        if (typeof Plyr !== 'undefined') {
          window.clearInterval(timer);
          callback();
          return;
        }

        if (Date.now() - startedAt > 8000) {
          window.clearInterval(timer);
          console.error('Plyr is not defined.');
        }
      }, 50);
    }

    isVideoVisible(video) {
      return window.getComputedStyle(video).display !== 'none';
    }

    bindHtml5Videos() {
      this.videos.forEach((video) => {
        video.muted = true;
        video.playsInline = true;
        video.loop = true;

        video.addEventListener(
          'loadeddata',
          () => {
            if (video.currentTime === 0 && Number.isFinite(video.duration)) {
              video.currentTime = Math.min(0.001, video.duration);
            }
          },
          { once: true }
        );

        if (video.readyState === HTMLMediaElement.HAVE_NOTHING) {
          video.load();
        }
      });
    }

    initYoutube() {
      if (!this.plyrHost || this.player) return;

      const mobileId = this.plyrHost.getAttribute('data-plyr-embed-id-mobile');
      if (mobileId && window.innerWidth <= 850) {
        this.plyrHost.setAttribute('data-plyr-embed-id', mobileId);
      }

      this.player = new Plyr(this.plyrHost, {
        autoplay: true,
        loop: { active: true },
        controls: [],
        tooltips: { controls: false, seek: false },
        muted: true,
        youtube: {
          rel: 0,
          showinfo: 0,
          iv_load_policy: 3,
          modestbranding: 1,
          playsinline: 1,
        },
      });

      this.player.on('ready', () => {
        this.player.muted = true;
        this.player.volume = 0;
        this.player.play();
      });
    }

    observeViewport() {
      this.viewportObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.2) {
            this.play();
          } else {
            this.pause();
          }
        },
        {
          threshold: [0, 0.2, 0.5, 1],
        }
      );

      this.viewportObserver.observe(this);
    }

    play() {
      if (this.mediaType === 'video') {
        this.videos.forEach((video) => {
          if (this.isVideoVisible(video)) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
        return;
      }

      if (this.mediaType === 'youtube' && this.player) {
        this.player.muted = true;
        this.player.play();
      }
    }

    pause() {
      if (this.mediaType === 'video') {
        this.videos.forEach((video) => video.pause());
        return;
      }

      if (this.mediaType === 'youtube' && this.player) {
        this.player.pause();
      }
    }
  }

  customElements.define('page-campaign-video', PageCampaignVideo);
}
